import React from 'react';

import {assert} from 'Asserts';
import {RegistrySummary, SavingState} from 'constants/RegistryConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {graphQLArrayToREST, graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {AllStorableSchemasQuery} from 'registry/graphql/__generated/AllStorableSchemasQuery';
import {
  RegistrySummaryQuery,
  RegistrySummaryQueryVariables,
} from 'registry/graphql/__generated/RegistrySummaryQuery';
import {ALL_STORABLE_SCHEMAS_QUERY} from 'registry/graphql/AllStorableSchemas';
import {REGISTRY_SUMMARY_QUERY} from 'registry/graphql/RegistrySummary';
import {StorableSchema} from 'samples/storables/StorableSchemaConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

export interface StorableSchemaSettingProps {
  registryId: string;
  ownerUrlId: string;
  savingState: SavingState;
  isBeingCreated: boolean;
  isWritable: boolean;
}

export interface StorableSchemaSettingsQueryingProps {
  storableSchemas: Array<StorableSchema>;
  registry: RegistrySummary;
}

export function useQueryingStorableSchemaSettings(
  props: StorableSchemaSettingProps,
  settingsComponentFn: (queryingProps: StorableSchemaSettingsQueryingProps) => JSX.Element | null
): JSX.Element | null {
  const allStorableSchemasResult = useBenchlingQuery<AllStorableSchemasQuery>(ALL_STORABLE_SCHEMAS_QUERY);
  const registrySummaryResult = useBenchlingQuery<RegistrySummaryQuery, RegistrySummaryQueryVariables>(
    REGISTRY_SUMMARY_QUERY,
    {
      variables: {registryId: props.registryId},
    }
  );

  if (
    allStorableSchemasResult.type === QueryResultType.PENDING ||
    registrySummaryResult.type === QueryResultType.PENDING
  ) {
    return <HorizontalThrobber />;
  } else if (
    allStorableSchemasResult.type === QueryResultType.SUCCESS &&
    registrySummaryResult.type === QueryResultType.SUCCESS
  ) {
    const queryBoxSchemas = allStorableSchemasResult.data.boxSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryContainerSchemas = allStorableSchemasResult.data.containerSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryLocationSchemas = allStorableSchemasResult.data.locationSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    const queryPlateSchemas = allStorableSchemasResult.data.plateSchemas.filter(
      (schema) => schema.registry.id === props.registryId
    );
    assert(!isInaccessible(registrySummaryResult.data.registry), 'Expecting registry to be accessible');

    return settingsComponentFn({
      storableSchemas: [
        ...graphQLArrayToREST('PlateSchema', queryPlateSchemas),
        ...graphQLArrayToREST('BoxSchema', queryBoxSchemas),
        ...graphQLArrayToREST('ContainerSchema', queryContainerSchemas),
        ...graphQLArrayToREST('LocationSchema', queryLocationSchemas),
      ],
      registry: graphQLToREST('RegistrySummary', registrySummaryResult.data.registry),
    });
  } else {
    return null;
  }
}
